import api, { getAccessToken } from "@/lib/api";

const RAW = process.env.REACT_APP_BACKEND_URL;

/**
 * Attach (or fetch, if it already exists) the evidence pack for a chat answer.
 * The backend generates one automatically when the message is stored, but this
 * lets the UI ensure it exists (idempotent) and drives the "Attach evidence"
 * affordance for older messages that predate the feature.
 */
export async function attachEvidence({
  workspaceId,
  answerId,
  question,
  sql,
  plan,
  chart,
  role,
  restrictedColumns,
  rowCount,
  sourceKind = "chat",
  conversationId,
  reportId,
  checkId,
}) {
  const { data } = await api.post("/evidence/attach", {
    workspace_id: workspaceId,
    answer_id: answerId,
    question: question || "",
    sql: sql || "",
    plan: plan || {},
    chart: chart || null,
    role: role || "viewer",
    restricted_columns: restrictedColumns || [],
    row_count: rowCount ?? null,
    source_kind: sourceKind,
    conversation_id: conversationId || null,
    report_id: reportId || null,
    check_id: checkId || null,
  });
  return data;
}

export async function getEvidence(answerId) {
  const { data } = await api.get(`/evidence/${encodeURIComponent(answerId)}`);
  return data;
}

export function evidencePdfUrl(answerId) {
  return `${RAW}/api/evidence/${encodeURIComponent(answerId)}/export.pdf`;
}

export function evidenceJsonUrl(answerId) {
  return `${RAW}/api/evidence/${encodeURIComponent(answerId)}/export.json`;
}

/**
 * Trigger a download of an authenticated evidence export. Fetches with the
 * Bearer token, blobs the response and hands it to the browser as an
 * `<a download>` click. Falls back to opening in a new tab if the blob
 * approach fails.
 */
export async function downloadEvidence(answerId, format /* 'pdf' | 'json' */) {
  const url = format === "pdf" ? evidencePdfUrl(answerId) : evidenceJsonUrl(answerId);
  const token = getAccessToken();
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = `evidence-${answerId.replace(/[^a-zA-Z0-9]+/g, "_")}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

/** Mint a public share link for an evidence pack. */
export async function createShare(answerId, expiresInDays = 7) {
  const { data } = await api.post(
    `/evidence/${encodeURIComponent(answerId)}/share`,
    { expires_in_days: expiresInDays },
  );
  return data;
}

/** List every share ever created for `answerId` — active + expired + revoked. */
export async function listShares(answerId) {
  const { data } = await api.get(`/evidence/${encodeURIComponent(answerId)}/shares`);
  return data;
}

/** Revoke a share by its token. Public view will start returning 410. */
export async function revokeShare(token) {
  const { data } = await api.post(`/evidence/shares/${encodeURIComponent(token)}/revoke`);
  return data;
}

/** Build the canonical public-facing share URL for a token. */
export function publicShareUrl(token) {
  return `${window.location.origin}/e/${token}`;
}

/** Public (no-auth) fetch used by the /e/:token share view. */
export async function fetchPublicShare(token) {
  const res = await fetch(`${RAW}/api/e/${encodeURIComponent(token)}`);
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    const err = new Error(detail.detail || `Share unavailable (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export function publicSharePdfUrl(token) {
  return `${RAW}/api/e/${encodeURIComponent(token)}/export.pdf`;
}
